import * as React from 'react'
import { connectAutoComplete } from 'react-instantsearch-dom'
import AutoSuggest from 'react-autosuggest'
import styled from 'styled-components'
import { colors } from '../shared/colors'
import type { TrackType } from '../../flowTypes'
import { GoodSansB2 } from '../shared/typography'
import SeriesIcon from '@components/shared/icons/SeriesIcon'
import TagIcon from '@components/shared/icons/TagIcon'
import StoriesIcon from '@components/shared/icons/StoriesIcon'

type HitType = {
  index: 'tags' | 'tracks' | 'categories' | 'series',
  hits: TrackType[]
}
type HitsType = HitType[]
type PropsType = {
  hits: any,
  refine: any,
  currentRefinement: string,
  searchActive: boolean,
  selectTag: (id: string | null | undefined) => void,
  selectCategory: (id: string | null | undefined) => void,
  selectSeries: (id: string | null | undefined) => void
}
type SuggestionTagType = {
  name: string,
  objectID: string,
  slugs: {
    [slug: string]: 1
  }
}
type AlgoliaTrackType = TrackType & {
  objectID: string,
  slugs: {
    [key: string]: 1
  },
  display_name: string,
  subType: string,
  description: string,
  name: string
}
type AlgoliaCategoryType = {
  display_name: string,
  objectID: string,
  subType: string,
  id: string,
  description: string
}
type AlgoliaSeriesType = {
  name: string,
  objectID: string,
  id: string,
  description: string
}
type SubSuggestionType =
  | SuggestionTagType
  | AlgoliaTrackType
  | AlgoliaCategoryType
  | AlgoliaSeriesType
type SuggestionSelectedType = {
  method: 'click' | 'enter',
  sectionIndex: number,
  suggestionValue: string,
  suggestionIndex: number,
  suggestion: SubSuggestionType
}

class AutoComplete extends React.PureComponent<
  PropsType,
  { value?: string, suggestions?: any[] }
> {
  constructor (props: PropsType) {
    super(props)
    this.state = {
      value: this.props.currentRefinement,
      suggestions: []
    }
    this.renderSuggestion = this.renderSuggestion.bind(this)
  }

  componentDidUpdate (prevProps: any, _prevState: any, _snapshot: any): void {
    if (this.props.hits !== prevProps.hits) {
      const newHits = this.filterHits(this.props.hits)
      this.setState({
        suggestions: newHits,
        value: this.props.currentRefinement
      })
    }
  }

  filterHits (hits: HitsType): HitsType {
    const newHits: any[] = []
    let tagHits: Record<string, any> = {}
    let trackHits: Record<string, any> = {}
    let categoryHits: Record<string, any> = {}
    let seriesHits: Record<string, any> = {}
    hits.forEach((hit: HitType): void => {
      if (hit.index === 'tags') {
        tagHits = hit
      } else if (hit.index === 'categories') {
        categoryHits = hit
      } else if (hit.index === 'series') {
        seriesHits = hit
      } else if (hit.index === 'tracks') {
        trackHits = {
          index: 'tracks',
          hits: hit.hits
        }
      }
    })
    if (Object.keys(tagHits).length) {
      newHits.push(tagHits)
    }
    if (Object.keys(categoryHits).length) {
      newHits.push(categoryHits)
    }
    if (Object.keys(seriesHits).length) {
      newHits.push(seriesHits)
    }
    if (Object.keys(trackHits).length) {
      newHits.push(trackHits)
    }
    return newHits
  }

  onChange = (event: any, { newValue }: any): void => {
    this.setState({
      value: newValue
    })
  }

  onSuggestionsFetchRequested = ({ value }: any): void => {
    this.props.refine(value)
  }

  onSuggestionsClearRequested = (): void => {
    this.setState({
      suggestions: []
    })
  }

  onSuggestionSelected = (
    event: any,
    suggestion: SuggestionSelectedType
  ): void => {
    // The suggestion union's members have disjoint fields and are told apart by
    // duck-typing below, which TypeScript cannot narrow without a discriminant.
    const picked = suggestion.suggestion as any
    const value = suggestion.suggestionValue || this.state.value
    if (
      picked &&
      picked.slugs &&
      suggestion.sectionIndex === 0
    ) {
      this.props.selectTag(picked.objectID)
    } else if (
      picked &&
      picked.subType &&
      picked.display_name
    ) {
      this.props.selectCategory(picked.objectID)
    } else if (
      picked &&
      picked.name &&
      picked.description
    ) {
      this.props.selectSeries(picked.objectID)
    }
    this.setState({
      value,
      suggestions: []
    })
  }

  getSuggestionValue (hit: any): any {
    if (hit) {
      return hit.name || hit.title || hit.display_name
    }
    return null
  }

  renderSuggestion = (hit: any): React.ReactNode => {
    if (hit) {
      if (hit.title2 || hit.title) {
        return (
          <HitContainer>
            <IconContainer>
              <StoriesIcon
                size={16}
                color={colors.white}
              />
            </IconContainer>
            <p>{hit.title2 || hit.title}</p>
          </HitContainer>
        )
      }
      if (hit.display_name) {
        // check for hunk, use star
        return (
          <HitContainer>
            <IconContainer>
              <TagIcon
                size={16}
                color={colors.white}
              />
            </IconContainer>
            <p>{hit.display_name}</p>
          </HitContainer>
        )
      }
      if (hit.name) {
        if (!hit.slugs) {
          return (
            <HitContainer>
              <IconContainer>
                <SeriesIcon
                  size={16}
                  color={colors.white}
                />
              </IconContainer>
              <p>{hit.name}</p>
            </HitContainer>
          )
        } else {
          return (
            <HitContainer>
              <IconContainer>
                <TagIcon
                  size={16}
                  color={colors.white}
                />
              </IconContainer>
              <p>{hit.name}</p>
            </HitContainer>
          )
        }
      }
    }
    return null
  }

  renderSectionTitle (_section: any): any {
    return null
  }

  getSectionSuggestions (section: any): any {
    if (section?.hits) {
      return section.hits
    }
    return []
  }

  render (): React.ReactNode {
    const { hits } = this.props
    const { value } = this.state
    const inputProps = {
      'aria-label': 'Search tags, stories, and more in the Dipsea library',
      placeholder: 'Scottish Accent, WLW, Fantasy...',
      onChange: this.onChange,
      value: value || ''
    }
    const hasStoryHits = hits.filter((hit: any): boolean => hit.hits.length).length > 0

    return (
      <Container>
        <AutoSuggest
        {...({} as any)}
          highlightFirstSuggestion={true}
          suggestions={this.state.suggestions}
          multiSection={true}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps}
          renderSectionTitle={this.renderSectionTitle}
          getSectionSuggestions={this.getSectionSuggestions}
        />

        {!hasStoryHits && (
          <EmptySearchContainer>
            <p>{`Hmmm... we're coming up empty.`}</p>
          </EmptySearchContainer>
        )}
      </Container>
    )
  }
}

export default connectAutoComplete(AutoComplete)

const Container = styled.div<any>`
    li {
        &[aria-selected='true'] {
            button {
                background-color: ${colors.white10};
            }
        }
    }
`
const EmptySearchContainer = styled.div<any>`
    margin: 0;
    padding: 20px 40px;
    p {
        ${GoodSansB2};
    }
`
const HitContainer = styled.button<any>`
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    outline: none;
    width: 100%;
    p {
        ${GoodSansB2};
        padding: 0.5em 10px 0.5em 0;
    }
`
const IconContainer = styled.div<any>`
  width: 34px ;
`
